import _split from 'lodash/split';
import type { NavApplicationItemType, NavItem, NavItems } from '../../types';

export type GetActiveNavItemProps = {
  navItems: NavItems;
  activePathnames: string[];
};

function sanitisePathName(path: string) {
  const adaptedPath = _split(path, '?')[0];

  return adaptedPath.endsWith('/') ? adaptedPath : `${adaptedPath}/`;
}

export const PROJECTS_ROOT_PATH = '/projects';

// Todo: Navigation
/**
 * Since we havent changed the routes yet,
 * For items that have been moved from settings to AI Governance,
 * If the path and ActivePathnames match any of following combination,
 * we do not show Settings as active item even when url matches it.
 */
const PATH_TO_ACTIVE_PATHNAMES_MAP = [
  {
    path: '/settings/',
    activePathnames: [
      '/settings/security',
      '/settings/alerts',
      '/settings/change-sets/outbound',
      '/settings/change-sets/inbound',
      '/settings/change-sets/connected-environments',
      '/settings/change-sets/tags',
      '/settings/theme-builder',
    ],
  },
  {
    // matches path: '/p/0/ai-setup/',
    path: /^\/p\/\d+\/ai-setup\/?$/,
    activePathnames: [
      '/ai-setup/sensitive-information',
      // matches path: '/p/0/ai-setup/sensitive-information'
      /^\/p\/\d+\/ai-setup\/sensitive-information/,

      // matches path: 'p/0/ai-setup/people-directory'
      // since we do not have seperate route for People
      '/ai-setup/people-directory',
      /^\/p\/\d+\/ai-setup\/people-directory/,
    ],
  },
  {
    path: '/p/0/automations/',
    activePathnames: [
      '/automations/templates',
      '/p/0/automations/templates',
      '/p/0/automations/templates',
    ],
  },
  {
    // matches path: '/p/0/interfaces/custom',
    path: /^\/p\/\d+\/interfaces\/?$/,
    activePathnames: [
      // matches path: '/p/0/interfaces/custom-components'
      /^\/p\/\d+\/interfaces\/custom-components/,
      /^\/p\/\d+\/interfaces\/template-components/,
      // matches path: '/p/0/interfaces/forms'
      /^\/p\/\d+\/interfaces\/manager\/forms/,
      // matches path: '/p/0/interfaces/fields'
      /^\/p\/\d+\/interfaces\/manager\/fields/,
    ],
  },
];

export const getIsPathActive = ({
  activePathnames,
  path,
}: {
  activePathnames: string[];
  path: string;
}): boolean => {
  const sanitizedPath = sanitisePathName(path);

  const itemWithPath = PATH_TO_ACTIVE_PATHNAMES_MAP.find((item) => {
    if (typeof item.path === 'string') {
      return item.path === sanitizedPath;
    }
    return item.path.test(sanitizedPath);
  });

  const pathNameMatches = itemWithPath?.activePathnames.some((_item: string | RegExp) =>
    activePathnames.some((pathname) => {
      const sanitizedPathname = sanitisePathName(pathname);
      if (typeof _item === 'string') {
        // Handle both string and regex patterns
        return sanitizedPathname.startsWith(_item);
      }
      return _item.test(sanitizedPathname);
    }),
  );

  if (pathNameMatches) return false;

  return activePathnames.some((pathname) => {
    const activePath = sanitisePathName(pathname);
    return activePath.startsWith(sanitizedPath);
  });
};

// Check for exact match, otherwise check for prefix match
export const getActiveNavItem = ({
  navItems,
  activePathnames,
}: GetActiveNavItemProps): NavItem | NavApplicationItemType | undefined => {
  for (const item of navItems) {
    if (item.type === 'URL' || item.type === 'APPLICATION_PAGE') {
      const exactMatch = activePathnames.includes(item.url);
      const prefixMatch = getIsPathActive({ activePathnames, path: item.url });

      if (exactMatch || prefixMatch) {
        return item;
      }
    } else if (item.type === 'GROUP' && item.items) {
      const activeItem = getActiveNavItem({
        navItems: item.items,
        activePathnames,
      });
      if (activeItem) {
        return activeItem;
      }
    }
  }

  return undefined;
};
